// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNotNil, mergeRight } from 'ramda';
import moment from 'moment';

// MUI
import { GridRenderCellParams, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';

// Core
import auth from '../../../../core/authorization';
import {
    ParameterValues,
    paramRepository,
    ResourceTextApplication,
} from '../../../../core/resources';
import { dayFormat } from '../../../../core/constants';

// Common
import { ADataGrid } from '../../../../common/dataGrid';
import { ADeleteIconButton } from '../../../../common/buttons';
import { AGridColumns, gridColumns } from '../../../../common/dataGrid';
import { TextWithTooltip } from '../../../../common/typography';

// Interfaces
import { AnnouncementTypeEnum, DialogModeEnum } from '../../../../interfaces/enums';
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementAnimal,
    IAnimalAnnouncementBatch,
    ICollectWeek,
} from '../../../../interfaces/IAnnouncement';
import { IAnimalSummary, IAnimalSummaryExtraInfo } from '../../../../interfaces/IAnimal';
import { IFarmDetails } from '../../../../interfaces/IBusinessEntities';

// Store
// API

// Feature
import AnimalAnnouncementSalesTypeField from './AnimalAnnouncementSalesTypeField';
import ConfirmDialog from '../../../shared/components/confirm/ConfirmDialog';
import {
    adminCanUpdateAnimalAnnouncement,
    birthDateComparator,
    euIdentifierComparator,
    isAnimalAnnouncementBatchRowSelectable,
    mapAnimalSummaryToAnnouncementDialog,
    mapIAnimalAnnouncementBatchToAnnouncementDialog,
} from '../../helpers/data';
import { collectWeekString } from '../../helpers/collectWeek';
import { AnimalAnnouncementCollectWeekField } from './AnimalAnnouncementCollectWeekField';
import { IProducerReturn } from '../../../../interfaces/IProducerReturn';

export interface IAnimalAnnouncementDataGridProps {
    announcementType: AnnouncementTypeEnum;
    mode: DialogModeEnum;
    animalAnnouncementData: IAnimalAnnouncement | null;
    initiallySelectedAnimals: IAnimalAnnouncementAnimal[] | null;
    allAnimals: IAnimalSummaryExtraInfo[] | null;
    allowedSalesTypes: ParameterValues | null;
    editSalesType: (animalId: string) => void;
    handleSelectedAnimals: (animalData: IAnimalSummary[]) => void;
    handleAnimalRemoveFromAnnouncement: (
        animalData: IAnimalAnnouncementBatch,
        animalAnnouncementNumber?: number
    ) => void;
    loading: boolean;
    readOnly?: boolean;
    farmDetails: IFarmDetails | undefined;
    producerReturn: IProducerReturn | null;
}

// animal list in announcement dialog
export default function AnimalAnnouncementDataGrid(props: IAnimalAnnouncementDataGridProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaGeneral',
        'AnelmaLayout',
    ]);
    const dataGridApiRef = useGridApiRef();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [bovineSexParams, setBovineSexParams] = useState<ParameterValues>([]);
    const [allSalesTypeParams, setAllSalesTypeParams] = useState<ParameterValues>([]);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [initiallySelectedAnimals, setInitiallySelectedAnimals] = useState<
        IAnimalAnnouncementAnimal[] | null
    >([]);
    const [initialAnimalAnnouncementData, setInitialAnimalAnnouncementData] =
        useState<IAnimalAnnouncement | null>(props.animalAnnouncementData);
    const [selectedAnimals, setSelectedAnimals] = useState<IAnimalSummary[]>([]);
    const [animalBatchToRemoveFromGrid, setAnimalBatchToRemoveFromGrid] =
        useState<IAnimalAnnouncementBatch | null>(null);

    const bovineSlaughterAnnouncementColumns: AGridColumns = [
        {
            field: 'EuIdentifier',
            headerName: t('AnelmaBovine:1005'),
            renderCell: (params) => (
                <TextWithTooltip
                    text={params.row.Animals.map((_: IAnimalAnnouncementAnimal) => {
                        return _.EuIdentifier;
                    })}
                />
            ),
            valueGetter: (params, row) => {
                return (
                    row.Animals?.map((_: IAnimalAnnouncementAnimal) => {
                        return _.EuIdentifier;
                    }) ?? row.EuIdentifier
                );
            },
            width: 150,
            sortable: true,
            sortComparator: (first, second) => euIdentifierComparator(first, second),
        },
        {
            field: 'EarNumber',
            headerName: t('AnelmaBovine:1007'),
            width: 60,
            renderCell: (params) => <TextWithTooltip text={String(params.value ?? '')} />,
            valueGetter: (params, row) =>
                getEarNumberValue(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'BirthDate',
            headerName: t('AnelmaBovine:1011'),
            renderCell: (params) => {
                const birthDate = String(params.value ?? '');
                return (
                    <TextWithTooltip text={birthDate} />
                );
            },
            valueGetter: (params, row) => {
                const birthDate = row.Animals?.[0]?.BirthDate ?? row.BirthDate;
                return birthDate ? moment(String(birthDate)).format(dayFormat) : '';
            },
            width: 90,
            sortable: true,
            sortComparator: (first, second) => birthDateComparator(first, second),
        },
        {
            field: 'Age',
            headerName: t('AnelmaBovine:1012'),
            width: 70,
            type: 'number',
            renderCell: (params) =>
                formatBovineAge(getBovineAgeValue(params.row.Animals as IAnimalAnnouncementAnimal[])),
            valueGetter: (params, row) =>
                getBovineAgeValue(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'Sex',
            headerName: t('AnelmaBovine:1048'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return bovineSexParams.find((param) => {
                    if (param.code === String(row.Animals[0].Sex)) {
                        return param.text;
                    }
                })?.text;
            },
            width: 60,
        },
        {
            field: 'SalesType',
            headerName: `${t('AnelmaBovine:1019')} *`,
            renderCell: (params) => {
                const salesTypeValue = getSalesTypeCode(params.row);

                return salesTypeValue !== null &&
                    !Number.isNaN(salesTypeValue) ? (
                    <AnimalAnnouncementSalesTypeField
                        id={params.row.id}
                        salesType={salesTypeValue}
                        animalAnnouncementType={props.announcementType}
                        allowedSalesTypes={props.allowedSalesTypes}
                        editSalesType={() => {
                            props.editSalesType(params.row.id);
                        }}
                    />
                ) : (
                    ''
                );
            },
            valueGetter: (params, row) => {
                return getSalesTypeText(getSalesTypeCode(row));
            },
            width: 100,
        },
        {
            field: 'AnimalGroup',
            headerName: t('AnelmaBovine:1142'),
            renderCell: (params) => <TextWithTooltip text={String(params.value ?? '')} />,
            valueGetter: (params, row) => getGroupText(row.Animals as IAnimalAnnouncementAnimal[]),
            width: 90,
        },
        {
            field: 'Week',
            headerName: `${t('AnelmaBovine:1097')} *`,
            renderCell: (params) => {
                return (
                    <AnimalAnnouncementCollectWeekField
                        collectWeekStart={params.row.CollectWeekStart}
                        collectWeekEnd={params.row.CollectWeekEnd}
                        mode={props.mode}
                    />
                );
            },
            valueGetter: (params, row) =>
                collectWeekString(row.CollectWeekStart, row.CollectWeekEnd),
            width: 110,
        },
        {
            field: 'MatureForSlaughterDate',
            headerName: t('AnelmaBovine:1094'),
            width: 80,
            renderCell: (params) =>
                getMatureForSlaughterDate(params.row.Animals as IAnimalAnnouncementAnimal[]),
            valueGetter: (params, row) =>
                getMatureForSlaughterDate(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'BatchStatus',
            headerName: t('AnelmaBovine:1035'),
            renderCell: (params: GridRenderCellParams) => {
                return params.value !== null && params.value !== undefined ? (
                    <TextWithTooltip text={String(params.value)} />
                ) : (
                    ''
                );
            },
            valueGetter: (params, row) => row.BatchStatus ?? '',
            width: 70,
        },
        gridColumns.singleSelectText(
            props.mode === DialogModeEnum.Create ? 'Info' : 'BatchInfo',
            [],
            t('AnelmaBovine:1180'),
            80
        ),
    ];

    const bovineIntermediationAnnouncementColumns: AGridColumns = [
        {
            field: 'EuIdentifier',
            headerName: t('AnelmaBovine:1005'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return (
                    row.Animals?.map((_: IAnimalAnnouncementAnimal) => {
                        return _.EuIdentifier;
                    }) ?? row.EuIdentifier
                );
            },
            width: 150,
            sortable: true,
            sortComparator: (first, second) => euIdentifierComparator(first, second),
        },
        {
            field: 'EarNumber',
            headerName: t('AnelmaBovine:1007'),
            width: 60,
            renderCell: (params) => <TextWithTooltip text={String(params.value ?? '')} />,
            valueGetter: (params, row) =>
                getEarNumberValue(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'BirthDate',
            headerName: t('AnelmaBovine:1011'),
            renderCell: (params) => {
                const birthDate = String(params.value ?? '');
                return (
                    <TextWithTooltip text={birthDate} />
                );
            },
            valueGetter: (params, row) => {
                const birthDate = row.Animals?.[0]?.BirthDate ?? row.BirthDate;
                return birthDate ? moment(String(birthDate)).format(dayFormat) : '';
            },
            width: 90,
            sortable: true,
            sortComparator: (first, second) => birthDateComparator(first, second),
        },
        {
            field: 'Age',
            headerName: t('AnelmaBovine:1012'),
            type: 'number',
            width: 70,
            renderCell: (params) =>
                formatBovineAge(getBovineAgeValue(params.row.Animals as IAnimalAnnouncementAnimal[])),
            valueGetter: (params, row) =>
                getBovineAgeValue(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'Sex',
            headerName: t('AnelmaBovine:1048'),
            renderCell: (params) => {
                return <TextWithTooltip text={String(params.value)} />;
            },
            valueGetter: (params, row) => {
                return bovineSexParams.find((param) => {
                    if (param.code === String(row.Animals[0].Sex)) {
                        return param.text;
                    }
                })?.text;
            },
            width: 60,
        },
        {
            field: 'SalesType',
            headerName: `${t('AnelmaBovine:1019')} *`,
            renderCell: (params) => {
                const salesTypeValue = getSalesTypeCode(params.row);

                return salesTypeValue !== null &&
                    !Number.isNaN(salesTypeValue) ? (
                    <AnimalAnnouncementSalesTypeField
                        id={params.row.id}
                        salesType={salesTypeValue}
                        animalAnnouncementType={props.announcementType}
                        allowedSalesTypes={props.allowedSalesTypes}
                        editSalesType={() => {
                            props.editSalesType(params.row.id);
                        }}
                    />
                ) : (
                    ''
                );
            },
            valueGetter: (params, row) => getSalesTypeText(getSalesTypeCode(row)),
            width: 100,
        },
        {
            field: 'AnimalGroup',
            headerName: t('AnelmaBovine:1142'),
            renderCell: (params) => <TextWithTooltip text={String(params.value ?? '')} />,
            valueGetter: (params, row) => getGroupText(row.Animals as IAnimalAnnouncementAnimal[]),
            width: 90,
        },
        {
            field: 'Week',
            headerName: `${t('AnelmaBovine:1097')} *`,
            renderCell: (params) => {
                return (
                    <AnimalAnnouncementCollectWeekField
                        collectWeekStart={params.row.CollectWeekStart}
                        collectWeekEnd={params.row.CollectWeekEnd}
                        mode={props.mode}
                    />
                );
            },
            valueGetter: (params, row) =>
                collectWeekString(row.CollectWeekStart, row.CollectWeekEnd),
            width: 110,
        },
        {
            field: 'TransmissionAgeDate',
            headerName: t('AnelmaBovine:1095'),
            width: 80,
            renderCell: (params) =>
                getTransmissionAgeDate(params.row.Animals as IAnimalAnnouncementAnimal[]),
            valueGetter: (params, row) =>
                getTransmissionAgeDate(row.Animals as IAnimalAnnouncementAnimal[]),
        },
        {
            field: 'BatchStatus',
            headerName: t('AnelmaBovine:1035'),
            renderCell: (params: GridRenderCellParams) => {
                return params.value !== null && params.value !== undefined ? (
                    <TextWithTooltip text={String(params.value)} />
                ) : (
                    ''
                );
            },
            valueGetter: (params, row) => row.BatchStatus ?? '',
            width: 80,
        },
        gridColumns.singleSelectText('BatchInfo', [], t('AnelmaBovine:1039'), 80),
    ];

    const isRemoveAnimalDisabled = (params: GridRenderCellParams): boolean => {
        const adminCanUpdateAnnouncement = adminCanUpdateAnimalAnnouncement(
            props.animalAnnouncementData,
            props.announcementType
        );

        let rowSelectable = isAnimalAnnouncementBatchRowSelectable(
            props.announcementType,
            params.row.CollectWeekStart as ICollectWeek,
            adminCanUpdateAnnouncement,
            params.row.BatchStatus
        );

        if (props.loading)
            return true;

        // remove producer return first
        let euIdentifier = '';
        if (params.row.Animals && (params.row.Animals as IAnimalAnnouncementAnimal[]).length === 1)
            euIdentifier = (params.row.Animals as IAnimalAnnouncementAnimal[])[0].EuIdentifier;
        else if (params.row.EuIdentifier)
            euIdentifier = params.row.EuIdentifier;

        if (props.producerReturn?.EUIdentifier === euIdentifier)
            return true;

        // ...
        if (props.readOnly && props.readOnly === true) return true;
        if (
            (props.announcementType === AnnouncementTypeEnum.Intermediation ||
                props.announcementType ===
                    AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) &&
            props.mode === 'edit'
        ) {
            return !auth.canModifyBovineBrokerSales || !rowSelectable;
        }
        if (props.mode === 'edit') return !rowSelectable;

        return false;
    };

    const getColumns = (): AGridColumns => {
        if (
            props.announcementType === AnnouncementTypeEnum.Slaughter ||
            props.announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
        )
            return bovineSlaughterAnnouncementColumns;
        else if (
            props.announcementType === AnnouncementTypeEnum.Intermediation ||
            props.announcementType ===
                AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
        )
            return bovineIntermediationAnnouncementColumns;
        else return [];
    };

    useEffect(() => {
        paramRepository.load(['AnelmaBovine']).then(() => {
            setBovineSexParams(paramRepository.resource('AnelmaBovine', 'BovineSex'));
            setAllSalesTypeParams(paramRepository.resource('AnelmaBovine', 'SalesType'));
        });
    }, []);

    useEffect(() => {
        props.handleSelectedAnimals(selectedAnimals);
    }, [selectedAnimals]);

    useEffect(() => {
        setInitiallySelectedAnimals(props.initiallySelectedAnimals);
    }, [props.initiallySelectedAnimals]);

    useEffect(() => {
        setInitialAnimalAnnouncementData(props.animalAnnouncementData);
    }, [props.animalAnnouncementData]);

    useEffect(() => {
        setIsLoading(props.loading);
    }, [props.loading]);

    const getEuIdentifierValue = (row: IAnimalAnnouncementBatch): string => {
        const euIdentifiers =
            row.Animals?.map((animal: IAnimalAnnouncementAnimal) => animal.EuIdentifier).filter(
                (euIdentifier: string | undefined) => !!euIdentifier
            ) ?? [];

        if (euIdentifiers.length > 0)
            return euIdentifiers.join(', ');

        return (row as { EuIdentifier?: string }).EuIdentifier ?? '';
    };

    const getGroupText = (animals: IAnimalAnnouncementAnimal[]) => {
        if (isNotNil(props.allAnimals) && isNotNil(animals)) {
            const matchingAnimal = props.allAnimals.find((a) => a.Id === animals[0].AnimalId);
            return matchingAnimal?.Group ?? '';
        }
        return '';
    };

    const getSalesTypeCode = (row: any): number | null => {
        const value = row.Animals?.[0]?.SalesType ?? row.SalesType;

        if (value === undefined || value === null || value === '')
            return null;

        const salesType = Number(value);
        return Number.isNaN(salesType) ? null : salesType;
    };

    const getSalesTypeText = (salesTypeCode: number | null): string => {
        if (salesTypeCode === null)
            return '';

        const salesTypeTextFromAllParams = allSalesTypeParams.find(
            (salesType) => Number(salesType.code) === salesTypeCode
        )?.text;

        if (salesTypeTextFromAllParams)
            return salesTypeTextFromAllParams;

        return (
            props.allowedSalesTypes?.find((salesType) => Number(salesType.code) === salesTypeCode)
                ?.text ?? ''
        );
    };

    const getEarNumberValue = (animals: IAnimalAnnouncementAnimal[]): string => {
        if (isNotNil(props.allAnimals) && isNotNil(animals)) {
            const matchingAnimal = props.allAnimals.find((a) => a.Id === animals[0].AnimalId);

            if (matchingAnimal)
                return parseEarNumber(matchingAnimal.EarNumber);
        }

        return '';
    };

    const parseEarNumber = (value: number | null): string => {
        if (!value || value === -1)
            return '';
        return value.toString().padStart(4, '0');
    };

    const getBovineAgeValue = (animals: IAnimalAnnouncementAnimal[]): number | null => {
        if (isNotNil(props.allAnimals) && isNotNil(animals)) {
            const matchingAnimal = props.allAnimals.find((a) => a.Id === animals[0].AnimalId);

            if (matchingAnimal && isNotNil(matchingAnimal.AgeMonths))
                return Math.round(Number(matchingAnimal.AgeMonths) * 10) / 10;
        }

        return null;
    };

    const formatBovineAge = (age: number | null): string => {
        if (age === null)
            return '';

        return age.toFixed(1);
    };

    const getMatureForSlaughterDate = (animals: IAnimalAnnouncementAnimal[]) => {
        if (isNotNil(props.allAnimals) && isNotNil(animals)) {
            const matchingAnimal = props.allAnimals.find((a) => a.Id === animals[0].AnimalId);
            return matchingAnimal?.MatureForSlaughterDate ?? '';
        }
        return '';
    };

    const getTransmissionAgeDate = (animals: IAnimalAnnouncementAnimal[]) => {
        if (isNotNil(props.allAnimals) && isNotNil(animals)) {
            const matchingAnimal = props.allAnimals.find((a) => a.Id === animals[0].AnimalId);
            return matchingAnimal?.TransmissionAgeDate ?? '';
        }
        return '';
    } 

    const setAnimalDataForDataGridRows = () => {
        if (initialAnimalAnnouncementData !== null) {
            return initialAnimalAnnouncementData.Batches.filter((a) => a.Animals.length > 0).map(
                (_) => mergeRight(_, { id: _.Animals[0].AnimalId })
            );
        } else if (initiallySelectedAnimals !== null && props.mode === DialogModeEnum.Create) {
            return initiallySelectedAnimals.map((_) => mergeRight(_, { id: _.AnimalId }));
        }
        return [];
    };

    const getActionTooltipText = (): string => {
        if (
            props.announcementType === AnnouncementTypeEnum.Slaughter ||
            props.announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
        )
            return t('AnelmaBovine:1045');
        else if (
            props.announcementType === AnnouncementTypeEnum.Intermediation ||
            props.announcementType ===
                AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
        )
            return t('AnelmaBovine:1045');
        else return '';
    };

    const handleAnimalRemoveConfirmed = () => {
        if (!animalBatchToRemoveFromGrid) return;
        if (props.animalAnnouncementData) {
            props.handleAnimalRemoveFromAnnouncement(
                animalBatchToRemoveFromGrid,
                props.animalAnnouncementData.Number
            );
        } else {
            props.handleAnimalRemoveFromAnnouncement(animalBatchToRemoveFromGrid);
        }
        setAnimalBatchToRemoveFromGrid(null);
    };

    const isRowSelectable = (params: GridRowParams): boolean => {
        const tempAnimal = params.row as IAnimalAnnouncementBatch;
        const adminCanUpdateAnnouncement = adminCanUpdateAnimalAnnouncement(
            props.animalAnnouncementData,
            props.announcementType
        );

        if (
            animalBatchToRemoveFromGrid?.Animals[0].AnimalId === tempAnimal?.Animals[0]?.AnimalId &&
            !initialAnimalAnnouncementData?.Batches.find(
                (i) => i.Animals[0].AnimalId === tempAnimal.Animals[0].AnimalId
            )
        ) {
            return false;
        }
        if (props.readOnly && props.readOnly === true) return false;
        if (props.mode === 'edit')
            return isAnimalAnnouncementBatchRowSelectable(
                props.announcementType,
                params.row.CollectWeekStart as ICollectWeek,
                adminCanUpdateAnnouncement,
                params.row.BatchStatus
            );

        return true;
    };

    return (
        <>
            <ADataGrid
                disableSelectionOnClick={true}
                actions={[
                    (params: GridRenderCellParams) => {
                        return (
                            <ADeleteIconButton
                                key={`${params.id}-delete`}
                                onClick={() => {
                                    setConfirmDialogVisible(true);

                                    const animal = params.row as IAnimalAnnouncementBatch;

                                    if (animal) {
                                        setAnimalBatchToRemoveFromGrid(animal ?? null);
                                    }
                                }}
                                tooltip={getActionTooltipText()}
                                disabled={isRemoveAnimalDisabled(params)}
                            />
                        );
                    },
                ]}
                columns={getColumns()}
                rows={setAnimalDataForDataGridRows()}
                onSelectionModelChange={(selectionModel) =>
                    setSelectedAnimals(
                        props.mode === DialogModeEnum.Create
                            ? mapAnimalSummaryToAnnouncementDialog(
                                  props.allAnimals as IAnimalSummary[],
                                  selectionModel
                              )
                            : props.mode === DialogModeEnum.Edit &&
                              initialAnimalAnnouncementData !== null
                            ? mapAnimalSummaryToAnnouncementDialog(
                                  mapIAnimalAnnouncementBatchToAnnouncementDialog(
                                      initialAnimalAnnouncementData.Batches
                                  ),
                                  selectionModel
                              )
                            : []
                    )
                }
                enableFiltering
                loading={isLoading}
                isRowSelectable={(params) => isRowSelectable(params)}
                apiRef={dataGridApiRef}
            />

            {confirmDialogVisible && (
                <ConfirmDialog
                    title={'AnelmaGeneral:1142'}
                    content={'AnelmaBovine:1062'}
                    actionButtonType={'delete'}
                    onClose={() => {
                        setConfirmDialogVisible(false);
                    }}
                    onConfirm={handleAnimalRemoveConfirmed}
                />
            )}
        </>
    );
}
